import React, { useContext, useEffect, useRef, useState } from "react";
import { ArrowUp } from "assets/ArrowUp";
import { ArrowDown } from "assets/ArrowDown";
import GridPageLayout from "core/components/layout/grid/gridLayout/gridPageLayout";
import { t } from "i18next";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { setReduxFrameworkData } from "modules/ac2/store/alert";
import { AuthContext } from "core/contexts/auth/auth";
import { getCasesMetrics } from "modules/ac2/services/alertService";
import { getFrameworkData, mapFrameworkData } from "modules/ac2/services/complianceService";
import { useAppTheme } from "core/contexts/theme/theme";
import CasesEmergeny from "assets/CasesEmergeny";
import CasesHigh from "assets/CasesHigh";
import CasesInformational from "assets/CasesInformational";
import CasesLow from "assets/CasesLow";
import CasesMedium from "assets/CasesMedium";
import { Search } from "@mui/icons-material";
import CheckboxChecked from "assets/CheckboxChecked";
import CheckboxUnchecked from "assets/CheckboxUnchecked";
import { useTheme } from "styled-components";
import { useNavigate } from "react-router";
import InfoIcon from "./InfoIcon";
import {
  ComplianceContainer,
  FrameworkWrapper,
  FrameworkSelect,
  HeaderControls,
  SectionTitle,
  SectionDivider,
  FilterButton,
  ExportButton,
  TableContainer,
  ComplianceTableEl,
  TableHead,
  SectionRow,
  SectionTitle2,
  ComplianceBox,
  PassedBox,
  ProgressBar,
  ProgressFill,
  FindingsTable,
  FindingsTableHead,
  FindingsTableHeadCell,
  FindingsTableRow,
  FindingsTableCell,
  SeverityCell,
  ViewLink,
  FilterPopup,
  SearchBox,
  SearchInput,
  FilterGroup,
  FilterGroupTitle,
  ApplyFilterButton,
  CircleArrowWrapper,
  InfoIconWrapper,
  InfoTooltip,
  PolicyUpdateBox,
  PolicyUpdateTitle,
  PolicyUpdateText,
  FullDescription
  
} from "./complianceTable.styled";
import { setBreadCrumbs, setHeader } from "core/store/shared/header";
import { SidebarContext } from "core/contexts/sidebar/sidebar";
import { navigateToPath } from "core/utils/common";
import { setActiveModel } from "modules/chat/store/conversationList";
 
const frameworkOptions = [
  { value: "CIS Benchmarks", label: "CIS Benchmarks" },
  { value: "DORA", label: "DORA" },
  { value: "GxP EU Annex 11", label: "GxP EU Annex 11" },
  { value: "HIPAA", label: "HIPAA" },
  { value: "NIST 800 Rev 5", label: "NIST 800 Rev 5" }
];

// Initial filter structure
const initialFilters = {
  "Provider": [
    { label: "AWS", value: "aws" },
    { label: "Azure", value: "azure" },
    { label: "GCP", value: "gcp" }
  ],
  "Type": [
    { label: "Bucket", value: "bucket" },
    { label: "Compute", value: "compute" },
    { label: "IAM", value: "iam" },
    { label: "Database", value: "database" },
    { label: "Logging", value: "logging" },
    { label: "Network", value: "network" },
    { label: "Backup", value: "backup" },
    { label: "Encryption", value: "encryption" },
    { label: "General", value: "general" }
  ]
};

export default function ComplianceTable() {
  const [expandedSections, setExpandedSections] = React.useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const toggleFilter = () => setShowFilters(!showFilters);
  const [selectedFramework, setSelectedFramework] = useState(frameworkOptions[0].value);
  const [frameworkData, setFrameworkData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [ruleNames, setRuleNames] = useState<{label: string, value: string}[]>([]);
  
  const handleFilterChange = (category: string, value: string) => {
    setSelectedFilters(prev => {
      const prevCategory = prev[category] || [];
      
      // If already selected, remove it
      if (prevCategory.includes(value)) {
        return {
          ...prev,
          [category]: prevCategory.filter(v => v !== value)
        };
      }
      
      // Otherwise add it
      return {
        ...prev,
        [category]: [...prevCategory, value]
      };
    });
  };
  
  const isFilterSelected = (category: string, value: string) => {
    return selectedFilters[category]?.includes(value) || false;
  };

  const filterRef = useRef<HTMLDivElement>(null);

  // Load framework data when selected framework changes
  useEffect(() => {
    const loadFrameworkData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Reset expanded sections when framework changes
        setExpandedSections([]);
        
        const { controls, rules } = await getFrameworkData(selectedFramework);
        const mappedData = mapFrameworkData(controls, rules, selectedFramework);
        setFrameworkData([mappedData]);
        
        // Extract unique rule names for filters
        if (Array.isArray(rules?.data)) {
          const uniqueRuleNames = new Set<string>();
          
          rules.data.forEach((rule: any) => {
            if (rule.rule_name) {
              uniqueRuleNames.add(rule.rule_name);
            }
          });
          
          const ruleNameOptions = Array.from(uniqueRuleNames).map(name => ({
            label: formatRuleName(name),
            value: name
          }));
          
          setRuleNames(ruleNameOptions);
          
          // Update filters with rule names
          setFilters(prev => ({
            ...prev,
            "Rules": ruleNameOptions
          }));
        }
      } catch (err) {
        console.error('Error loading framework data:', err);
        setError('Failed to load framework data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    console.log(`Framework name: ${selectedFramework}`)
    
    loadFrameworkData();
  }, [selectedFramework]);
  
  // Helper function to format rule names
  const formatRuleName = (ruleName: string): string => {
    if (!ruleName) return '';
    
    // Replace hyphens and underscores with spaces
    let formattedName = ruleName.replace(/[-_]/g, ' ');
    
    // Capitalize each word
    formattedName = formattedName.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    return formattedName;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  // Filter the table data based on selected filters and search term
  const getFilteredData = () => {
    if (!frameworkData.length) return [];
    
    const hasActiveFilters = Object.values(selectedFilters).some(filters => filters.length > 0);
    const hasSearchTerm = searchTerm.trim().length > 0;
    
    // If no filters and no search term, return original data
    if (!hasActiveFilters && !hasSearchTerm) {
      return frameworkData;
    }
    
    return frameworkData.map((frameworkBlock: any) => {
      // Filter sections
      const filteredData = frameworkBlock.data.map((section: any) => {
        // Filter findings within each section
        const filteredFindings = section.findings.filter((finding: any) => {
          // Apply search term filter
          if (hasSearchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const descriptionMatch = finding.description.toLowerCase().includes(searchLower);
            const typeMatch = finding.type.toLowerCase().includes(searchLower);
            const severityMatch = finding.severity.toLowerCase().includes(searchLower);
            
            if (!(descriptionMatch || typeMatch || severityMatch)) {
              return false;
            }
          }
          
          // Apply category filters
          if (hasActiveFilters) {
            // Check Provider filter
            if (selectedFilters["Provider"]?.length > 0 && 
                !selectedFilters["Provider"].includes((finding.provider || "aws").toLowerCase())) {
              return false;
            }
            
            // Check Type filter
            if (selectedFilters["Type"]?.length > 0 && 
                !selectedFilters["Type"].includes(finding.type.toLowerCase())) {
              return false;
            }
            
            // Check Rules filter
            if (selectedFilters["Rules"]?.length > 0 && 
                !selectedFilters["Rules"].includes(finding.rawRuleName)) {
              return false;
            }
          }
          
          return true;
        });
        
        return {
          ...section,
          findings: filteredFindings
        };
      }).filter((section: any) => section.findings.length > 0);
      
      return {
        ...frameworkBlock,
        data: filteredData
      };
    }).filter((frameworkBlock: any) => frameworkBlock.data.length > 0);
  };
  
  const filteredData = getFilteredData();

  const theme = useTheme();

  const navigate = useNavigate();

  interface Finding {
    description: string;
    severity: string;
    type: string;
    compliance: string;
    passfail: string;
    guidance?: string;
    rawRuleName?: string;
  }

  interface Section {
    id: number;
    title: string;
    passed: string;
    compliance: string;
    findings: Finding[];
  }
  const breadcrumbs = useAppSelector((state) => state.header.crumbs);

  //const breadcrumbs = useAppSelector((state) => state.header.crumbs);

  const handleViewClick = (section: Section, framework: string, description: string, rawRuleName: string, data: Section) => {
    const descriptions = section.findings.map(finding => finding.description);
    dispatch(setBreadCrumbs([...breadcrumbs, { displayText: "Cases", path: "/console/cp/ac2/framework" }]));

    // Update Redux store instead of localStorage
    const frameworkDataPayload = {
      descriptions: descriptions,
      selectedDescription: description,
      selectedRawRuleName: rawRuleName,
      selectedFramework: framework,
      selectedSectionTitle: data.title
    };
    dispatch(setReduxFrameworkData(frameworkDataPayload));
    console.log("Clicked on View button for section findings:", section.findings.map(finding => finding.description));
    console.log("Section Title:", data.title);
    console.log("Finding Description:", description);
    console.log("Raw Rule Name:", rawRuleName);
    console.log("Framework:", framework);
    
    navigate("/console/cp/ac2/framework");
    
  };

  const toggleSection = (id: number) => {
    setExpandedSections(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const { checkAFToken } = useContext(AuthContext)
  const { apptheme } = useAppTheme();
  const dispatch = useAppDispatch();
  const alertState = useAppSelector((state) => state.alerts)

  const [topGridBoxdata, setTopGridBoxdata] = useState<any>([
    { num: 0, type: t("TOP_GRID_BOX.TOTAL_CASES"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.CRITICAL"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.HIGH"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.MEDIUM"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.LOW"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.INFORMATIVE"), color: "fff" },
  ]);

  const getSeverityIcon = (severity: string): JSX.Element | null => {
    const key = severity.toLowerCase();

    if (["critical", "fatal", "severe"].includes(key)) return <CasesEmergeny />;
    if (key === "high") return <CasesHigh />;
    if (key === "medium") return <CasesMedium />;
    if (key === "low") return <CasesLow />;
    if (["informational", "info", "other"].includes(key)) return <CasesInformational />;

    return null;
  };

  async function getGridBoxData() {
    const token = await checkAFToken();
    const config = { 'Authorization': token, 'Content-Type': 'application/json' };
    const gridBoxData = await getCasesMetrics(config);
    setTopGridBoxdata(gridBoxData);
  }

  /**
   * Update the gridData
   */
  useEffect(() => {
    getGridBoxData();
  }, [alertState.pageEvents])


  return (
    <>
      <div className="p-3"><GridPageLayout {...topGridBoxdata} /></div>

      <ComplianceContainer>

        {/* Header Controls */}
        <HeaderControls>
          <FrameworkWrapper>
            <SectionTitle>Framework</SectionTitle>
            <div style={{ position: "relative" }}>
              <FrameworkSelect
                value={selectedFramework}
                onChange={(e) => setSelectedFramework(e.target.value)}
                aria-label="Select framework"
                className="framework-select"
              >
                {frameworkOptions.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FrameworkSelect>
              <div style={{ 
                position: "absolute", 
                top: "1.35rem", 
                right: "1.25rem", 
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${theme.colors.light || "#460073"}10, ${theme.colors.light || "#460073"}05)`,
                backdropFilter: "blur(4px)",
              }}>
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L5 5L9 1" stroke={theme.colors.light || "#460073"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </FrameworkWrapper>
          
          <div style={{ display: "flex", gap: "12px", position: "relative" }}>
            <FilterButton onClick={toggleFilter} type="button" className="">
              Filters
            </FilterButton>
            
            {showFilters && (
              <FilterPopup ref={filterRef}>
                {/* Search */}
                <SearchBox>
                  <Search style={{ width: "20px", height: "20px", color: theme.colors.textDisabledColor || "#96968C" }} />
                  <SearchInput
                    type="text"
                    placeholder="Search all fields"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SearchBox>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ 
                    background: theme.colors.highlight2 || '#AF24FF', 
                    width: '4px', 
                    height: '16px', 
                    marginRight: '8px',
                    borderRadius: '2px'
                  }}></div>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    {Object.values(selectedFilters).flat().length > 0 ? 
                      `${Object.values(selectedFilters).flat().length} filters applied` : 
                      'No filters applied'}
                  </span>
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '0 4px' }}>
                  {Object.entries(filters).map(([group, options]) => (
                    <FilterGroup key={group}>
                      <FilterGroupTitle>{group}</FilterGroupTitle>
                      {options.length > 8 && (
                        <div style={{ margin: '8px 0 12px' }}>
                          <SearchBox style={{ marginBottom: 0 }}>
                            <Search style={{ width: "16px", height: "16px", color: theme.colors.textDisabledColor || "#96968C" }} />
                            <SearchInput
                              type="text"
                              placeholder={`Search ${group.toLowerCase()}`}
                              onChange={(e) => {}}
                            />
                          </SearchBox>
                        </div>
                      )}
                      <div style={group === "Rules" ? { maxHeight: '200px', overflowY: 'auto', marginBottom: '8px' } : {}}>
                        {options.map((option, idx) => (
                          <div key={idx} style={{ marginBottom: "8px" }}>
                            <label
                              style={{ display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer", marginBottom: "0.5rem" }}
                            >
                              <input
                                type="checkbox"
                                checked={isFilterSelected(group, option.value)}
                                onChange={() => handleFilterChange(group, option.value)}
                                style={{ display: "none" }}
                              />
                              <span style={{ height: "14px", display: "contents" }}>
                                <div style={{ transform: "scale(0.75)" }}>
                                  {isFilterSelected(group, option.value) ? <CheckboxChecked /> : <CheckboxUnchecked theme={theme} />}
                                </div>
                              </span>
                              <span>{option.label}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </FilterGroup>
                  ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
                  <button 
                    onClick={() => setSelectedFilters({})} 
                    style={{ 
                      background: 'transparent', 
                      border: 'none', 
                      color: theme.colors.highlight2 || '#AF24FF', 
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: '8px',
                      fontSize: '0.875rem'
                    }}
                  >
                    Clear All
                  </button>
                  <ApplyFilterButton onClick={() => setShowFilters(false)} type="button">
                    Apply Filters
                  </ApplyFilterButton>
                </div>
              </FilterPopup>
            )}
            
            <ExportButton type="button" className="">
              Export
            </ExportButton>
          </div>
        </HeaderControls>

        <SectionDivider>
          <SectionTitle>Regulation</SectionTitle>
        </SectionDivider>

        <TableContainer>
          <ComplianceTableEl style={{ tableLayout: "fixed" }}>
            <TableHead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px 0", width: "70%" }}>Document Section</th>
                <th style={{ textAlign: "left", padding: "8px 0", width: "15%" }}>Compliance</th>
                <th style={{ textAlign: "left", padding: "8px 0", width: "15%" }}>Passed</th>
              </tr>
            </TableHead>
          </ComplianceTableEl>

          {/* Loading State */}
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Loading framework data...
            </div>
          )}
          
          {/* Error State */}
          {error && !isLoading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
              {error}
            </div>
          )}
          
          {/* Table Body */}
          {!isLoading && !error && filteredData.map((frameworkBlock, index) => (
            <FrameworkWrapper key={index}>
              {/* Sections inside each framework */}
              {frameworkBlock.data.map(section => {
                const expanded = expandedSections.includes(section.id);
                return (
                  <div key={section.id} style={{ marginBottom: "24px" }}>
                    <SectionRow>
                      <SectionTitle2 
                        style={{ width: "65%" }} 
                        onClick={() => toggleSection(section.id)}
                        title={section.title} // Adding tooltip that shows on hover
                      >
                        {section.title}
                      </SectionTitle2>

                      {/* Compliance + Progress bar */}
                      <ComplianceBox 
                        style={{ width: "15%", justifyContent: "flex-start" }}
                        onClick={() => toggleSection(section.id)}
                      >
                        <div style={{ width: "2rem" }}>{section.compliance}</div>
                        <ProgressBar>
                          <ProgressFill width={section.compliance} />
                        </ProgressBar>
                      </ComplianceBox>

                      {/* Passed + arrow */}
                      <PassedBox 
                        style={{ width: "15%", justifyContent: "space-between" }}
                        onClick={() => toggleSection(section.id)}
                      >
                        <span>{section.passed}</span>
                        <CircleArrowWrapper>
                          {expanded ? <ArrowUp /> : <ArrowDown />}
                        </CircleArrowWrapper>
                      </PassedBox>
                    </SectionRow>

                    {/* Section Description + Findings Table */}
                    {expanded && (
                      <>
                        {/* Show full description when expanded */}
                        {section.description && (
                          <FullDescription style={{ 
                            margin: "0 0 1.5rem 1rem", 
                            padding: "1rem 1.25rem",
                            fontSize: "0.875rem",
                            lineHeight: "1.6",
                            borderLeft: `3px solid ${theme.colors.highlight2 || "#AF24FF"}`,
                            background: `linear-gradient(to right, ${theme.colors.highlight2 || "#AF24FF"}08, transparent)`,
                            borderRadius: "0 0.375rem 0.375rem 0",
                            position: "relative",
                            transition: "all 0.3s ease",
                            maxWidth: "90%",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                          }}>
                            {section.description}
                          </FullDescription>
                        )}
                    
                      <FindingsTable>
                        <FindingsTableHead>
                          <tr>
                            <FindingsTableHeadCell style={{ width: "35%", textAlign: "left" }}>Monitoring Rule</FindingsTableHeadCell>
                            <FindingsTableHeadCell style={{ width: "10%", textAlign: "left" }}>Provider</FindingsTableHeadCell>
                            {/* <FindingsTableHeadCell style={{ width: "10%", textAlign: "left" }}>Severity</FindingsTableHeadCell> */}
                            <FindingsTableHeadCell style={{ width: "15%", textAlign: "left" }}>Type</FindingsTableHeadCell>
                            <FindingsTableHeadCell style={{ width: "15%", textAlign: "left" }}>Compliance</FindingsTableHeadCell>
                            <FindingsTableHeadCell style={{ width: "15%", textAlign: "left" }}>Passed</FindingsTableHeadCell>
                          </tr>
                        </FindingsTableHead>

                        <tbody>
                          {section.findings.map((finding, i) => (
                            <FindingsTableRow key={i}>
                              <FindingsTableCell style={{ paddingLeft: "2rem" }}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  {finding.guidance && (
                                    <div style={{ marginRight: "8px", cursor: "pointer" }}>
                                      <div style={{ position: "relative" }}>
                                        <InfoIconWrapper>
                                          <InfoIcon />
                                          <InfoTooltip>
                                            <PolicyUpdateBox className="golden-ratio-tooltip">
                                              <PolicyUpdateTitle>Rule Guidance</PolicyUpdateTitle>
                                              <PolicyUpdateText>
                                                <p>{finding.guidance || "This rule ensures that all public access is blocked for the S3 bucket. Public access settings at the account level provide centralized control over the public access to all S3 buckets. When configured correctly, this setting helps prevent data breaches caused by misconfigured bucket permissions and accidental public exposure of sensitive data."}</p>
                                              </PolicyUpdateText>
                                            </PolicyUpdateBox>
                                          </InfoTooltip>
                                        </InfoIconWrapper>
                                      </div>
                                    </div>
                                  )}
                                  {finding.description}
                                </div>
                              </FindingsTableCell>
                              
                              <FindingsTableCell>{finding.provider || "AWS"}</FindingsTableCell>

                              {/* <SeverityCell>
                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  {getSeverityIcon(finding.severity)}
                                  <span>{finding.severity}</span>
                                </div>
                              </SeverityCell> */}

                              <FindingsTableCell>{finding.type}</FindingsTableCell>

                              <FindingsTableCell>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "160px", justifyContent: "flex-start" }}>
                                  <span style={{ whiteSpace: "nowrap", width: "2rem" }}>{finding.compliance}</span>
                                  <ProgressBar>
                                    <ProgressFill width={finding.compliance} />
                                  </ProgressBar>
                                </div>
                              </FindingsTableCell>

                              <FindingsTableCell>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <span style={{ fontWeight: "bold" }}>{finding.passed}</span>

                                  {(finding.passfail !== "PASSED" && finding.passfail !== "NOT_AVAILABLE") && (
                                    <ViewLink
                                      onClick={() => handleViewClick(section, frameworkBlock.framework, finding.description, finding.rawRuleName, frameworkBlock.data[0])}
                                    >
                                      View
                                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
                                        <path d="M2.5 6H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M6 2.5L9.5 6L6 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </ViewLink>
                                  )}
                                </div>
                              </FindingsTableCell>
                            </FindingsTableRow>
                          ))}
                        </tbody>
                      </FindingsTable>
                      </>
                    )}
                  </div>
                );
              })}
            </FrameworkWrapper>
          ))}
        </TableContainer>
      </ComplianceContainer>
    </>
  );
};